import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { JsonForms } from "@jsonforms/react";
import {
    materialRenderers,
    materialCells,
} from "@jsonforms/material-renderers";
import { JsonFormsRendererRegistryEntry, createAjv } from "@jsonforms/core";
import { useTranslation } from "react-i18next";
import { FormAnswerDto } from "../../../models/formAnswer/formAnswerListItemDto";
import {
    AttachmentReadOnlyRenderer,
    attachmentRendererTester,
} from "../../forms/attachmentRenderer";
import { useFetch } from "../../../hooks/useFetch";
import * as formAnswerService from "../../../services/formAnswerService";
import Grid2 from "@mui/material/Unstable_Grid2";

const allRenderers: JsonFormsRendererRegistryEntry[] = [
    ...materialRenderers,
    { tester: attachmentRendererTester, renderer: AttachmentReadOnlyRenderer },
];

const ajv = createAjv();
ajv.addFormat("attachment", true);

interface FormAnswerPreviewDialogProps {
    formAnswerId: string | null;
    formName: string;
    onClose: () => void;
}

export const FormAnswerPreviewDialog = ({
    formAnswerId,
    formName,
    onClose,
}: FormAnswerPreviewDialogProps) => {
    const { t } = useTranslation();
    const [answer, setAnswer] = useState<FormAnswerDto | null>(null);

    useFetch(
        (signal) => formAnswerService.getFormAnswer(formAnswerId, signal),
        { onSuccess: (response) => setAnswer(response.data) },
        [formAnswerId]
    );

    const hasSchema = answer?.jsonSchema?.schema;

    return (
        <Dialog
            open={!!formAnswerId}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            data-testid="form-answer-preview-dialog"
        >
            <DialogTitle>
                <Grid2
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h2" data-testid="form-answer-preview-title">{formName}</Typography>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={onClose}
                        data-testid="form-answer-preview-close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid2>
            </DialogTitle>
            <DialogContent dividers>
                {!answer ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress />
                    </Box>
                ) : !hasSchema ? (
                    <Typography color="text.secondary">
                        {t("reporting.formSummary.noSchema")}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            pointerEvents: "none",
                            opacity: 0.9,
                            "& .MuiRadioGroup-root": { mb: 1, order: 1 },
                            "& [data-testid^='attachment-']": {
                                pointerEvents: "auto",
                            },
                        }}
                    >
                        <JsonForms
                            schema={answer.jsonSchema.schema}
                            uischema={answer.jsonSchema.uiSchema}
                            data={answer.jsonData}
                            renderers={allRenderers}
                            cells={materialCells}
                            config={{ showUnfocusedDescription: true }}
                            ajv={ajv}
                            readonly
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};
